import { CheckIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { SEARCH_RUN_STAGES, stageKey, type SearchRunState } from '@entities/search-run';
import { KarlIntro, ProgressBar } from '@shared/ui';

import styles from './SearchProgress.module.css';

interface SearchProgressProps {
  /** `null` — прогон ещё создаётся: этап неизвестен, но экран уже показан. */
  run: SearchRunState | null;
}

/**
 * Экран обработки запроса (FE-080, MVP_PRD §12).
 *
 * Показывает четыре этапа ровно в том порядке, в котором их выполняет worker:
 * анализируем требования → сопоставляем с каталогом → ранжируем автомобили →
 * готовим объяснения. Этап и процент приходят с backend при опросе статуса —
 * это реальный прогресс, а не таймер, который «дорисовывает» полосу.
 *
 * **Запрещённые формулировки** («ищем по всему интернету», «проверяем VIN»,
 * «анализируем миллионы объявлений») здесь невозможны: тексты этапов заданы
 * ключами, а под полосой стоит прямая оговорка о границах системы.
 *
 * Редизайн 2026-10: вводный абзац — реплика Karl (DESIGN_SYSTEM §7.2), этапы и
 * полоса — в одной карточке. Вращающегося значка у текущего этапа больше нет:
 * круглые спиннеры запрещены (§5.9), а движение здесь и так показывает
 * настоящая полоса прогресса.
 */
export function SearchProgress({ run }: SearchProgressProps) {
  const { t } = useTranslation();
  const titleId = useId();

  const currentStage = run?.progress.stage ?? null;
  const percent = Math.max(0, Math.min(100, run?.progress.percent ?? 0));
  const currentIndex = currentStage === null ? -1 : SEARCH_RUN_STAGES.indexOf(currentStage);

  return (
    <section className={styles.shell} aria-labelledby={titleId}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>{t('results.progress.eyebrow')}</p>
        <h1 id={titleId} className={styles.title}>
          {t('results.progress.title')}
        </h1>
        <KarlIntro>{t('results.progress.lead')}</KarlIntro>
      </header>

      <div className={styles.card}>
        {/* Полоса — вспомогательная: смысл несёт список этапов, а не проценты. */}
        <ProgressBar percent={percent} label={t('results.progress.title')} showValue />

        <ol className={styles.stages}>
          {SEARCH_RUN_STAGES.map((stage, index) => {
            const done = currentIndex > index || run?.status === 'completed';
            const active = currentIndex === index && run?.status === 'processing';
            return (
              <li
                key={stage}
                className={[styles.stage, done && styles.stageDone, active && styles.stageActive]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={active ? 'step' : undefined}
              >
                <span className={styles.stageIcon} aria-hidden="true">
                  {done ? <CheckIcon size={14} weight="bold" /> : <span className={styles.dot} />}
                </span>
                <span>{t(stageKey(stage))}</span>
                {/* Состояние этапа называется словом: точка и галочка сами по
                    себе ничего не сообщают скринридеру. */}
                <span className="visually-hidden">
                  {done
                    ? ` — ${t('results.progress.state.done')}`
                    : active
                      ? ` — ${t('results.progress.state.active')}`
                      : ` — ${t('results.progress.state.pending')}`}
                </span>
              </li>
            );
          })}
        </ol>

        <p className={styles.note}>{t('results.disclaimer')}</p>
      </div>
    </section>
  );
}
